import React, { useState } from 'react'

interface Note {
  id: string
  content: string
  authorName: string
  createdAt: string
}

interface InternalNotesProps {
  notes: Note[]
  onAddNote: (content: string) => Promise<void>
  loading?: boolean
}

export const InternalNotes: React.FC<InternalNotesProps> = ({ notes, onAddNote, loading = false }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [adding, setAdding] = useState(false)

  const handleAddNote = async () => {
    if (!newNote.trim()) return
    setAdding(true)
    try {
      await onAddNote(newNote.trim())
      setNewNote('')
    } catch (err) {
      console.error('Failed to add note:', err)
    } finally {
      setAdding(false)
    }
  }

  return (
    <div className="border-t border-border">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-txt-secondary hover:bg-surface-hover transition-colors"
      >
        <svg
          className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
        </svg>
        Internal Notes
        <span className="ml-auto px-1.5 py-0.5 rounded-full bg-surface-tertiary text-[10px] font-semibold text-txt-tertiary">
          {notes.length}
        </span>
      </button>

      {isExpanded && (
        <div className="px-4 pb-3 animate-slide-down">
          {/* Notes list */}
          <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
            {notes.length === 0 ? (
              <p className="text-xs text-txt-tertiary italic py-2">No internal notes yet</p>
            ) : (
              notes.map((note) => (
                <div key={note.id} className="p-2.5 rounded-lg bg-warning-light/30 border border-warning/10">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] font-semibold text-txt-primary">{note.authorName}</span>
                    <span className="text-[10px] text-txt-tertiary">{new Date(note.createdAt).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-txt-secondary leading-relaxed">{note.content}</p>
                </div>
              ))
            )}
          </div>

          {/* Add note input */}
          <div className="flex gap-2">
            <input
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
              placeholder="Add an internal note..."
              disabled={adding || loading}
              className="flex-1 px-3 py-2 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary placeholder-txt-tertiary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 transition-colors disabled:opacity-50"
            />
            <button
              onClick={handleAddNote}
              disabled={!newNote.trim() || adding || loading}
              className="px-3 py-2 rounded-lg bg-warning/10 text-warning font-medium text-sm hover:bg-warning/20 disabled:opacity-40 transition-colors"
            >
              {adding ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
